/*
 * album/viewer.ts - the full-screen photo viewer (GALLERY_PLAN.md §5, AL5) on the server-rendered
 * <dialog id="album_viewer">. Opens on any list of photos (a chapter, a chip's subset, a chapter's guest photos,
 * the guest wall, the grid): swipe (pointer events), ← →, Esc, preloads the neighbours, ▶ autoplay every 4 s,
 * counter, chapter badge, caption / "by", ♥, ⬇ the full file (album access only), 🔗 copy the D15 deep link.
 */

import { AlbumData, Favorites, Photo, chapterOf, deepLink, fmt, toast } from "./shared";

const AUTOPLAY_MS = 4000;
const SWIPE_PX = 40;
/** Ask the source for the next page this many photos before the end. */
const MORE_AHEAD = 3;

/**
 * A list that can grow (the Grid, AL6): the viewer's list is the source's own array; `more()` appends the next
 * page to it (false = nothing more), `total()` is the full count for the counter.
 */
export interface ViewerSource {
  more(): Promise<boolean>;
  total(): number;
}

export class Viewer {
  private list: Photo[] = [];
  private idx = 0;
  private source: ViewerSource | null = null;
  private loadingMore: Promise<boolean> | null = null;
  private timer = 0;
  private readonly img: HTMLImageElement;
  private readonly q = <T extends Element>(sel: string): T => this.dialog.querySelector<T>(sel)!;

  constructor(private readonly dialog: HTMLDialogElement, private readonly data: AlbumData, private readonly favs: Favorites) {
    this.img = this.q<HTMLImageElement>("[data-v-img]");

    this.q("[data-v-close]").addEventListener("click", () => this.close());
    this.q("[data-v-prev]").addEventListener("click", () => this.step(-1));
    this.q("[data-v-next]").addEventListener("click", () => this.step(1));
    this.q("[data-v-play]").addEventListener("click", () => this.setPlaying(!this.timer));
    this.q("[data-v-fav]").addEventListener("click", () => this.toggleFav());
    this.q("[data-v-link]").addEventListener("click", () => void this.copyLink());

    this.img.addEventListener("load", () => this.img.classList.remove("is-loading"));
    this.img.addEventListener("error", () => this.img.classList.remove("is-loading"));

    dialog.addEventListener("close", () => {
      this.setPlaying(false);
      document.documentElement.classList.remove("album-viewer-open");
    });
    // a click on the dark area around the photo (not on a control) closes, like the mock-up
    dialog.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;
      if (t === dialog || t.matches("[data-v-stage]")) this.close();
    });
    dialog.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); this.step(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); this.step(1); }
    });
    this.initSwipe(this.q<HTMLElement>("[data-v-stage]"));
    favs.onChange(() => this.paintFav());
  }

  get isOpen(): boolean {
    return this.dialog.open;
  }

  get current(): Photo | undefined {
    return this.list[this.idx];
  }

  open(list: Photo[], index: number, source: ViewerSource | null = null): void {
    if (!list.length) return;
    this.list = list;
    this.source = source;
    if (!this.dialog.open) {
      document.documentElement.classList.add("album-viewer-open");
      this.dialog.showModal();
    }
    this.show(index);
    this.q<HTMLElement>("[data-v-close]").focus({ preventScroll: true });
  }

  /**
   * Swaps the open list `from` for a fuller one holding the photo on screen (a Story tile opens at once on the tiles
   * shown, then on its whole chapter / sub-group once loaded); ignored once the viewer closed or moved to another list.
   */
  replace(from: Photo[], list: Photo[], source: ViewerSource | null = null): void {
    if (!this.dialog.open || this.list !== from) return;
    const i = list.indexOf(this.list[this.idx]);
    if (i < 0) return;
    this.list = list;
    this.source = source;
    this.show(i);
  }

  close(): void {
    if (this.dialog.open) this.dialog.close();
  }

  private step(d: number): void {
    // the end of a list that can grow: fetch the next page first instead of wrapping to the start
    if (d > 0 && this.idx === this.list.length - 1 && this.hasMore()) {
      const at = this.idx;
      void this.loadMore().then((added) => {
        if (this.dialog.open && this.idx === at) this.show(added ? at + 1 : 0);
      });
      return;
    }
    if (this.list.length < 2) return;
    this.show(this.idx + d);
    if (this.timer) this.setPlaying(true);   // restart the 4 s after a manual step
  }

  private hasMore(): boolean {
    return !!this.source && this.source.total() > this.list.length;
  }

  private loadMore(): Promise<boolean> {
    if (!this.source) return Promise.resolve(false);
    if (!this.loadingMore) {
      this.loadingMore = this.source.more()
        .catch(() => false)
        .then((added) => {
          this.loadingMore = null;
          if (this.dialog.open) this.paintCount();
          return added;
        });
    }
    return this.loadingMore;
  }

  private paintCount(): void {
    const n = Math.max(this.list.length, this.source?.total() ?? 0);
    const pad = String(n).length < 2 ? 2 : String(n).length;
    this.q("[data-v-count]").textContent = `${String(this.idx + 1).padStart(pad, "0")} / ${String(n).padStart(pad, "0")}`;
  }

  private show(i: number): void {
    const n = this.list.length;
    this.idx = ((i % n) + n) % n;
    const p = this.list[this.idx];

    if (this.img.getAttribute("src") !== p.full) {
      this.img.classList.add("is-loading");
      this.img.src = p.full;
    }
    this.img.alt = p.caption ?? "";

    this.paintCount();
    this.q("[data-v-badge]").textContent = this.badge(p);
    const cap = this.q<HTMLElement>("[data-v-caption]");
    cap.textContent = p.caption ?? "";
    cap.hidden = !p.caption;
    const by = this.q<HTMLElement>("[data-v-by]");
    by.textContent = p.guest && p.by ? fmt(this.data.i18n.by, p.by) : "";
    by.hidden = !by.textContent;

    // ⬇ is rendered only with album access
    const dl = this.dialog.querySelector<HTMLAnchorElement>("[data-v-download]");
    if (dl) {
      dl.href = p.full;
      dl.download = `wedding-${p.id}${(p.full.match(/\.[a-z0-9]+$/i) ?? [".jpg"])[0]}`;
    }

    const multi = n > 1 || this.hasMore();
    this.q<HTMLElement>("[data-v-prev]").hidden = !multi;
    this.q<HTMLElement>("[data-v-next]").hidden = !multi;
    this.q<HTMLElement>("[data-v-play]").hidden = !multi;

    this.paintFav();
    // preload the neighbours so a swipe shows at once
    if (multi) {
      [1, -1].forEach((d) => {
        const q = this.list[(this.idx + d + n) % n];
        if (q) new Image().src = q.full;
      });
    }
    if (this.idx >= n - MORE_AHEAD && this.hasMore()) void this.loadMore();
  }

  /** "Banquet › Cake cutting"; a guest photo outside a chapter: the guest wall's name. */
  private badge(p: Photo): string {
    const c = chapterOf(this.data, p.chapter) ?? (p.guest ? undefined : this.data.chapters.find((x) => x.auto));
    if (!c) return p.guest ? this.data.i18n.guests ?? "" : "";
    const s = p.subgroup != null ? c.subgroups.find((x) => x.id === p.subgroup) : undefined;
    return s?.name ? `${c.name} › ${s.name}` : c.name;
  }

  private paintFav(): void {
    const p = this.current;
    const btn = this.q<HTMLButtonElement>("[data-v-fav]");
    const on = !!p && this.favs.has(p.id);
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.classList.toggle("is-on", on);
    const icon = btn.querySelector("span");
    if (icon) icon.className = `${on ? "fa-solid" : "fa-regular"} fa-heart`;
  }

  private toggleFav(): void {
    const p = this.current;
    if (!p) return;
    const on = this.favs.toggle(p.id);
    const btn = this.q<HTMLElement>("[data-v-fav]");
    btn.classList.remove("is-beat");
    void btn.offsetWidth;   // restart the beat
    btn.classList.add("is-beat");
    toast(on ? this.data.i18n.favAdded : this.data.i18n.favRemoved);
  }

  private async copyLink(): Promise<void> {
    const p = this.current;
    if (!p) return;
    const url = deepLink(p);
    try {
      await navigator.clipboard.writeText(url);
      toast(this.data.i18n.linkCopied);
    } catch {
      // no clipboard (http, old browser, denied): leave the link in the address bar instead
      history.replaceState(history.state, "", url);
      toast(this.data.i18n.linkCopyFail);
    }
  }

  private setPlaying(on: boolean): void {
    window.clearInterval(this.timer);
    this.timer = 0;
    if (on && (this.list.length > 1 || this.hasMore())) this.timer = window.setInterval(() => this.autoStep(), AUTOPLAY_MS);
    const btn = this.q<HTMLButtonElement>("[data-v-play]");
    const playing = !!this.timer;
    btn.setAttribute("aria-pressed", playing ? "true" : "false");
    btn.setAttribute("aria-label", playing ? this.data.i18n.pause : this.data.i18n.play);
    const icon = btn.querySelector("span");
    if (icon) icon.className = `fa-solid ${playing ? "fa-pause" : "fa-play"}`;
  }

  /** ▶: the next photo; at the end of a growing list, its next page. */
  private autoStep(): void {
    if (this.idx === this.list.length - 1 && this.hasMore()) {
      const at = this.idx;
      void this.loadMore().then((added) => {
        if (this.dialog.open && this.idx === at) this.show(added ? at + 1 : 0);
      });
    } else {
      this.show(this.idx + 1);
    }
  }

  /** Horizontal swipe = previous / next; vertical moves are left to the browser (touch-action: pan-y). */
  private initSwipe(stage: HTMLElement): void {
    let x0 = 0;
    let y0 = 0;
    let id = -1;
    stage.addEventListener("pointerdown", (e) => {
      if ((e.target as HTMLElement).closest("button")) return;
      id = e.pointerId;
      x0 = e.clientX;
      y0 = e.clientY;
    });
    const end = (e: PointerEvent): void => {
      if (e.pointerId !== id) return;
      id = -1;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
        this.swiped = true;
        this.step(dx < 0 ? 1 : -1);
      }
    };
    stage.addEventListener("pointerup", end);
    stage.addEventListener("pointercancel", () => (id = -1));
    // the click that follows a swipe must not close the viewer
    stage.addEventListener("click", (e) => {
      if (this.swiped) {
        this.swiped = false;
        e.stopPropagation();
      }
    }, true);
  }

  private swiped = false;
}
